import { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { useSession } from "@/lib/auth";
import { ADMIN_HOME, isBareAdminEntry, isFirstRun } from "@/lib/loginTarget";

/**
 * Route-level session gate. Renders nothing while the session probe is in
 * flight, redirects to /login when unauthenticated, and only renders the
 * Outlet (child route element) when a session is confirmed.
 *
 * Why route-level: previously each admin page rendered AdminLayout, which
 * itself called useSession and redirected. The page component mounted FIRST,
 * fired its own useQuery / useEffect calls (which 401'd against the
 * session-required backend), THEN AdminLayout's redirect resolved. Result:
 * 3-4 noisy 401s in the console on every cold visit to /admin while logged
 * out. Wrapping the admin route group in this component means the page
 * component is never mounted until the session resolves, so the queries
 * don't fire.
 *
 * The /api/auth/me probe itself still 401s when not logged in — that's
 * structural. Browsers log a "Failed to load resource" for the 401 response,
 * which can't be silenced without changing the backend contract (e.g.,
 * returning 200 with a body flag instead of 401). One unavoidable console
 * line on the login redirect is the trade-off for the RESTful 401 shape.
 *
 * It is also the last line of defence for the first-run landing — see below.
 */
export function RequireAuth() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { data: session, isLoading } = useSession();

  // Belt and braces for the first-run tour. lib/loginTarget.ts fixes the
  // sign-in path and App.tsx's "/" route fixes the bare domain, but neither
  // covers a recruiter who is STILL signed in (the cookie lasts 30 days) and
  // opens a bookmarked /admin: no login happens, so no landing rule runs, and
  // the tour is mounted by /admin/home alone. That recruiter never sees it.
  //
  // Scoped to the bare /admin entry point ON PURPOSE. "Once per mount" is
  // once per FRESH PAGE LOAD, not once per session — RequireAuth stays
  // mounted across in-app <Link> navigation but remounts on every reload,
  // bookmark, typed URL or external link. Firing on any /admin/* path
  // therefore bounced every deep link a first-run recruiter opened: both
  // in-context-guide routes (/admin/new/ai, /admin/templates) were
  // unreachable by URL, and refreshing mid-composition threw away the
  // composer and dropped them on Home.
  //
  // /admin is the only path that still needs this. The bare domain is
  // handled by App.tsx's "/" route, and the sign-in path by
  // lib/loginTarget.ts; this covers only the remaining case its comment
  // describes — a recruiter STILL signed in (the cookie lasts 30 days) who
  // opens a bookmarked /admin, where no login happens so no landing rule
  // runs. Any deeper path is a deliberate destination and is left alone.
  //
  // It stops existing entirely once the tour is finished or skipped.
  //
  // Deliberately an EFFECT, not a render-time branch guarded by a ref: React
  // StrictMode double-invokes render in dev and keeps the SECOND result, so a
  // ref flipped during the first pass would make the second pass skip the
  // redirect — the guard would silently do nothing in dev and work in prod,
  // which is the worst possible way for this to behave. Refs survive
  // StrictMode's simulated remount, so latching inside the effect is safe.
  //
  // The cost is one frame of the bookmarked page before the redirect commits.
  // Acceptable: it happens once, on a page they're entitled to see anyway.
  const landingDecided = useRef(false);
  useEffect(() => {
    if (!session || landingDecided.current) return;
    landingDecided.current = true;
    if (isFirstRun(session) && isBareAdminEntry(pathname)) {
      navigate(ADMIN_HOME, { replace: true });
    }
  }, [session, pathname, navigate]);

  if (isLoading) {
    return <div className="min-h-screen bg-background" aria-busy="true" />;
  }
  if (!session) {
    // FEAT-47: the AI-assistant consent page is addressed by its query
    // (`/connect/authorize?rid=…`), so that one keeps its search string —
    // the admin deep links keep the bare path they always had.
    const from = pathname.startsWith("/connect/") ? pathname + search : pathname;
    return <Navigate to="/login" replace state={{ from }} />;
  }
  return <Outlet />;
}
